/**
 * @file 对局场景（tableScene）—— M4-T2 桌面的唯一渲染出口；M4-T3 对齐美术规格 08。
 *
 * ## 信息隔离红线（§10 / §11 E3）
 * 本模块只消费 `SeatView` 与调用方显式传入的 UI 状态（{@link TableUiState}）。
 * **绝不接触** `GameState.wall` / 他家手牌内容 / 听口。他家手牌只以张数
 * （`handCount`）渲染为牌背；他家暗杠（`kind === null`）渲染为 4 张牌背（D-14）。
 * 动效是对「已下发货的呈现」，不是推演。
 *
 * ## 幂等渲染
 * `render(view, ui)` 每次全量对账各区域（自家手牌/他家背面/弃牌河/副露/提示/按钮），
 * 相同输入产出相同场景图。
 *
 * ## 坐标单源（§11 E2）
 * 所有坐标来自 `layout.ts`（以美术规格 08 §3/§4 为唯一基准），1280×720 逻辑画布，
 * 运行时可整体缩放。任何「按当前分辨率直接写像素」都禁止。
 *
 * ## React 边界
 * 本文件不 import React。React 只负责挂载 canvas 与房间外壳（ADR-001）。
 */

import { Application, Container, Graphics, Rectangle, Sprite, Text, Texture } from 'pixi.js';
import type { MeldType, SeatIndex, SeatView, Suit, TileId, TileKind } from '@majiang/core';
import { kindOfTileId } from '@majiang/core';
import type { SceneAction, TableUiState } from '../session/types';
import { backSprite, SUIT_COLOR, TILE_H, TILE_W, tileSprite } from './tileArt';
import {
  ACTION,
  AREA,
  BASE_H,
  BASE_W,
  BUTTON_COLOR,
  CENTER_INFO,
  LEFT_HAND,
  NAME_FONT,
  RIGHT_HAND,
  RIVER,
  SELF_HAND,
  SELF_MELD,
  SETTLE,
  SIDE_MELD,
  TABLE_COLOR,
  TILE_SCALE,
  TOP_HAND,
  TOP_MELD,
  VOID_CARDS,
  WIN_TILE_POS,
} from './layout';
import { TweenRunner } from './tween';

const FONT_STACK = 'system-ui, -apple-system, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif';

const SUIT_LABEL: Record<Suit, string> = { wan: '万', tong: '筒', tiao: '条' };

/** 他家副露组内重叠步长（34×46 档，按 §4.4 自家步长等比缩放）。 */
const OPP_MELD_STEP = { peng: 22, gang: 17 } as const;

/** 用 canvas 测量文本宽度（Pixi v8 未导出 TextMetrics，浏览器场景可用）。 */
function measureTextWidth(text: string, fontSize: number, fontWeight: string): number {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (ctx === null) return text.length * fontSize * 0.62;
    ctx.font = `${fontWeight} ${fontSize}px system-ui, -apple-system, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif`;
    return ctx.measureText(text).width;
  } catch {
    return text.length * fontSize * 0.62;
  }
}

/** 圆形按钮（操作按钮，§4.5：120×44、圆角 8）。 */
function makeButton(label: string, width: number, height: number, onClick: () => void, fill: number, textFill: number): Container {
  const c = new Container();
  const bg = new Graphics();
  bg.roundRect(0, 0, width, height, ACTION.radius);
  bg.fill(fill);
  bg.stroke({ width: 1, color: 0x000000, alpha: 0.2 });
  const t = new Text({
    text: label,
    style: { fontSize: 20, fill: textFill, fontFamily: FONT_STACK, fontWeight: '700' },
  });
  t.anchor.set(0.5);
  t.position.set(width / 2, height / 2 + 1);
  c.addChild(bg, t);
  c.eventMode = 'static';
  c.cursor = 'pointer';
  c.hitArea = new Rectangle(0, 0, width, height);
  c.on('pointertap', onClick);
  return c;
}

/** 侧躺牌（补杠第 4 张，§4.4）：旋转 90°。返回占位宽。 */
function placeSideLie(sprite: Sprite, slotX: number, slotY: number, scale: number): number {
  sprite.anchor.set(0.5);
  sprite.rotation = Math.PI / 2;
  sprite.scale.set(scale);
  sprite.position.set(slotX + (TILE_H * scale) / 2, slotY + (TILE_W * scale) / 2);
  return TILE_H * scale;
}

export class TableScene {
  private readonly app: Application;
  private readonly root: Container;
  private readonly bg: Graphics;
  private readonly selfHandC: Container;
  private readonly topHandC: Container;
  private readonly leftHandC: Container;
  private readonly rightHandC: Container;
  private readonly riverC: Container;
  private readonly meldC: Container;
  private readonly promptC: Container;
  private readonly actionC: Container;
  private readonly wallC: Container;
  private readonly settleC: Container; // 结算面板专用容器（多局重开时整体清除）
  private readonly winningTileC: Container; // 胡牌翻牌容器（赢家面前展示 winningTile + 胡角标）
  private readonly claimedC: Container; // 顶部金色牌背堆（已胡玩家标记，2026-08-12 腾讯麻将风格）
  private readonly tween = new TweenRunner();
  private readonly tickerFn: (ticker: { deltaMS: number }) => void;

  private selfHandSprites = new Map<TileId, Sprite>();
  private actionHandler: ((a: SceneAction) => void) | null = null;
  private latestUi: TableUiState | null = null;
  private settledRendered = false;
  private destroyed = false;
  private bgTex: Texture | null = null;

  constructor(app: Application) {
    this.app = app;
    this.root = new Container();
    this.bg = new Graphics();
    this.selfHandC = new Container();
    this.topHandC = new Container();
    this.leftHandC = new Container();
    this.rightHandC = new Container();
    this.riverC = new Container();
    this.meldC = new Container();
    this.promptC = new Container();
    this.actionC = new Container();
    this.wallC = new Container();
    this.settleC = new Container();
    this.winningTileC = new Container();
    this.claimedC = new Container();

    this.root.addChild(
      this.bg,
      this.meldC,
      this.riverC,
      this.topHandC,
      this.leftHandC,
      this.rightHandC,
      this.selfHandC,
      this.wallC,
      this.claimedC, // 顶部已胡牌背（在 promptC 之下，避免压住倒计时）
      this.winningTileC,
      this.promptC,
      this.actionC,
      this.settleC,
    );
    this.app.stage.addChild(this.root);

    this.tickerFn = (ticker) => this.tween.update(ticker.deltaMS);
    this.app.ticker.add(this.tickerFn);
  }

  /** 挂在某个 DOM 容器下（由 GameScreen 调用；canvas 已在 App 初始化时挂载）。 */
  mount(container: HTMLElement): void {
    const canvas = this.app.canvas;
    if (canvas.parentElement !== container) container.appendChild(canvas);
    this.layout();
    window.addEventListener('resize', this.layout);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    window.removeEventListener('resize', this.layout);
    this.app.ticker.remove(this.tickerFn);
    this.tween.clear();
    this.app.stage.removeChild(this.root);
    this.root.destroy({ children: true });
    if (this.bgTex !== null) {
      try {
        this.bgTex.destroy(true);
      } catch {
        /* 纹理已随 renderer 销毁时忽略 */
      }
      this.bgTex = null;
    }
  }

  /** 响应式缩放：16:9 黑边居中（letterbox，§2.1）。 */
  private readonly layout = (): void => {
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const scale = Math.min(w / BASE_W, h / BASE_H);
    this.root.scale.set(scale);
    this.root.position.set((w - BASE_W * scale) / 2, (h - BASE_H * scale) / 2);
  };

  onAction(cb: (a: SceneAction) => void): void {
    this.actionHandler = cb;
  }

  /** 播放摸牌飞入动画（draw 事件的视觉反馈；无则跳过）。§7：从牌墙源点（1180,80）飞入。 */
  animateDraw(tileId: TileId): void {
    const sprite = this.selfHandSprites.get(tileId);
    if (sprite === undefined) return;
    const target = { x: sprite.x, y: sprite.y };
    const overlay = tileSprite(kindOfTileId(tileId), 1);
    overlay.position.set(1180, 80);
    overlay.alpha = 0;
    this.root.addChild(overlay);
    sprite.visible = false;
    this.tween.add(overlay, {
      from: { x: 1180, y: 80, alpha: 0, scaleX: 1.4 * TILE_SCALE.hand, scaleY: 1.4 * TILE_SCALE.hand },
      to: { x: target.x, y: target.y, alpha: 1, scaleX: TILE_SCALE.hand, scaleY: TILE_SCALE.hand },
      durationMs: 260,
      ease: 'out',
      onComplete: () => {
        sprite.visible = true;
        overlay.destroy({ children: true });
      },
    });
  }

  /**
   * 幂等渲染。`ui` 省略时（如纯结算视图）保留上次操作区。
   */
  render(view: SeatView, ui: TableUiState | null): void {
    if (ui !== null) this.latestUi = ui;
    this.drawBackground(view);
    this.renderHands(view);
    this.renderRivers(view);
    this.renderMelds(view);
    this.renderWall(view);
    this.renderClaimed(view);
    this.renderWinningTiles(view);
    this.renderPrompts(view);
    if (ui !== null) this.renderActionBar(view, ui);
    this.renderSettlement(view);
  }

  /* ─────────── 顶部金色牌背堆（已胡玩家标记，2026-08-12 腾讯麻将风格） ─────────── */

  /** 顶部 HUD 中央画一排金色牌背：每张牌背 = 该玩家当前 winningTile 数量（含自摸+点炮）。 */
  private renderClaimed(view: SeatView): void {
    this.claimedC.removeChildren();
    const records = this.collectWinRecords(view);
    const total = records.length;
    if (total === 0) return;
    const tileW = 26;
    const tileH = 38;
    const gap = 3;
    const totalW = total * tileW + (total - 1) * gap;
    const startX = BASE_W / 2 - totalW / 2;
    const y = 14;
    records.forEach((_, i) => {
      const g = new Graphics();
      g.roundRect(0, 0, tileW, tileH, 4);
      g.fill(0xf0a93a);
      g.stroke({ width: 1, color: 0x8a5c14 });
      // 内部菱形
      g.poly([tileW / 2, 5, tileW - 4, tileH / 2, tileW / 2, tileH - 5, 4, tileH / 2]);
      g.fill({ color: 0xc8831e, alpha: 0.85 });
      g.position.set(startX + i * (tileW + gap), y);
      this.claimedC.addChild(g);
    });
  }

  /* ─────────────── 背景（§5.1 径向渐变桌布） ─────────────── */

  /** 桌布径向渐变纹理（中心 (640,360)、半径 720；`--bg-table-0/1/2`）。 */
  private ensureBgTexture(): Texture | null {
    if (this.bgTex !== null) return this.bgTex;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = BASE_W;
      canvas.height = BASE_H;
      const ctx = canvas.getContext('2d');
      if (ctx === null) return null;
      const grad = ctx.createRadialGradient(BASE_W / 2, BASE_H / 2, 0, BASE_W / 2, BASE_H / 2, 720);
      grad.addColorStop(0, `#${TABLE_COLOR.bg2.toString(16).padStart(6, '0')}`);
      grad.addColorStop(0.5, `#${TABLE_COLOR.bg1.toString(16).padStart(6, '0')}`);
      grad.addColorStop(1, `#${TABLE_COLOR.bg0.toString(16).padStart(6, '0')}`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, BASE_W, BASE_H);
      this.bgTex = Texture.from(canvas);
      return this.bgTex;
    } catch {
      return null;
    }
  }

  private drawBackground(_view: SeatView): void {
    this.bg.clear();
    const tex = this.ensureBgTexture();
    if (tex !== null) {
      this.bg.rect(0, 0, BASE_W, BASE_H);
      this.bg.fill({ texture: tex });
    } else {
      this.bg.rect(0, 0, BASE_W, BASE_H);
      this.bg.fill(TABLE_COLOR.bg1);
    }
    // 2026-08-12 腾讯麻将风格：外深内亮径向 + 中央金色装饰圈
    const ring = new Graphics();
    ring.circle(BASE_W / 2, BASE_H / 2, 280);
    ring.stroke({ width: 2, color: TABLE_COLOR.gold, alpha: 0.25 });
    this.bg.addChild(ring);
    const ring2 = new Graphics();
    ring2.circle(BASE_W / 2, BASE_H / 2, 420);
    ring2.stroke({ width: 1, color: TABLE_COLOR.gold, alpha: 0.15 });
    this.bg.addChild(ring2);
  }

  /* ─────────────── 四家手牌区 ─────────────── */

  private renderHands(view: SeatView): void {
    const self = view.self;
    const ui = this.latestUi;
    const sorted = [...self.hand].sort((a, b) => a - b);
    const handScale = TILE_SCALE.hand;
    const tw = TILE_W * handScale;
    const th = TILE_H * handScale;

    // —— 自家（正面，可点选；点击即出，§4.5 P0）——
    this.selfHandC.removeChildren();
    this.selfHandSprites.clear();
    const n = sorted.length;
    const totalW = (n - 1) * SELF_HAND.step + tw;
    const startX = BASE_W / 2 - totalW / 2;
    sorted.forEach((id, i) => {
      const sprite = tileSprite(kindOfTileId(id), handScale);
      const slotX = startX + i * SELF_HAND.step;
      sprite.position.set(slotX, SELF_HAND.y);
      const isDisc = ui !== null && ui.canDiscard && ui.discardable.includes(id);
      // §7.5：轮到自己且手牌含定缺花色时，不可打花色降透明度
      if (ui !== null && ui.canDiscard && !isDisc) sprite.alpha = 0.45;
      // §4.6：可打牌描边提亮（金色）
      if (isDisc) {
        const hl = new Graphics();
        hl.roundRect(slotX - 2, SELF_HAND.y - 2, tw + 4, th + 4, 6);
        hl.stroke({ width: 2, color: TABLE_COLOR.highlight });
        this.selfHandC.addChild(hl);
      }
      sprite.eventMode = 'static';
      sprite.cursor = 'pointer';
      sprite.on('pointertap', () => this.onSelfTileTap(id));
      this.selfHandC.addChild(sprite);
      this.selfHandSprites.set(id, sprite);
    });
    this.selfHandC.addChild(
      this.nameRow(`我 · ${view.self.playerId.slice(0, 6)}`, view.scores[view.viewerSeat], view.self.voidSuit, BASE_W / 2, SELF_HAND.nameY, 'center'),
    );

    // —— 对家（背面，§4.1）——
    this.topHandC.removeChildren();
    const top = view.opponents.find((o) => o.seat === 2);
    if (top !== undefined) {
      const n2 = top.handCount;
      const totalW2 = (n2 - 1) * TOP_HAND.step + TILE_W * TILE_SCALE.side;
      const tStart = BASE_W / 2 - totalW2 / 2;
      for (let i = 0; i < n2; i++) {
        const s = backSprite(TILE_SCALE.side);
        s.position.set(tStart + i * TOP_HAND.step, TOP_HAND.y);
        this.topHandC.addChild(s);
      }
      this.topHandC.addChild(this.nameRow(this.playerName(top.seat, view), view.scores[top.seat], top.voidSuit, TOP_HAND.nameX, TOP_HAND.nameY, 'center'));
    }

    // —— 右家（竖排，§4.2 镜像）——
    this.rightHandC.removeChildren();
    const right = view.opponents.find((o) => o.seat === 1);
    if (right !== undefined) {
      const n3 = right.handCount;
      for (let i = 0; i < n3; i++) {
        const s = backSprite(TILE_SCALE.side);
        s.position.set(RIGHT_HAND.x, RIGHT_HAND.y + i * RIGHT_HAND.step);
        this.rightHandC.addChild(s);
      }
      this.rightHandC.addChild(this.nameRow(this.playerName(right.seat, view), view.scores[right.seat], right.voidSuit, RIGHT_HAND.nameX, RIGHT_HAND.nameY, 'center'));
    }

    // —— 左家（竖排，§4.2）——
    this.leftHandC.removeChildren();
    const left = view.opponents.find((o) => o.seat === 3);
    if (left !== undefined) {
      const n4 = left.handCount;
      for (let i = 0; i < n4; i++) {
        const s = backSprite(TILE_SCALE.side);
        s.position.set(LEFT_HAND.x, LEFT_HAND.y + i * LEFT_HAND.step);
        this.leftHandC.addChild(s);
      }
      this.leftHandC.addChild(this.nameRow(this.playerName(left.seat, view), view.scores[left.seat], left.voidSuit, LEFT_HAND.nameX, LEFT_HAND.nameY, 'center'));
    }
  }

  /** 自家手牌点击：点击即出（§4.5 P0）。只响应 `canDiscard` 且 `discardable` 内的牌。 */
  private onSelfTileTap(id: TileId): void {
    const ui = this.latestUi;
    if (ui === null || !ui.canDiscard) return;
    if (!ui.discardable.includes(id)) return;
    this.actionHandler?.({ type: 'discard', tileId: id });
  }

  /* ─────────────── 弃牌河四边带（§4.3） ─────────────── */

  /** 座位 → 弃牌带：下=自家、右=右家、上=对家、左=左家。 */
  private riverBand(
    seat: SeatIndex,
  ): { readonly x0: number; readonly y0: number; readonly stepX: number; readonly stepY: number; readonly cols: number; readonly rows: number; readonly capacity: number } | null {
    switch (seat) {
      case 0:
        return RIVER.bottom;
      case 1:
        return RIVER.right;
      case 2:
        return RIVER.top;
      case 3:
        return RIVER.left;
      default:
        return null;
    }
  }

  private renderRivers(view: SeatView): void {
    this.riverC.removeChildren();
    const players = [view.self, ...view.opponents];
    const scale = TILE_SCALE.river;
    for (const p of players) {
      const band = this.riverBand(p.seat);
      if (band === null) continue;
      // 超容量：丢弃最旧一张（§4.3）
      const shown = p.discards.slice(-band.capacity);
      shown.forEach((id, i) => {
        const col = i % band.cols;
        const row = Math.floor(i / band.cols);
        const sprite = tileSprite(kindOfTileId(id), scale);
        sprite.position.set(band.x0 + col * band.stepX, band.y0 + row * band.stepY);
        this.riverC.addChild(sprite);
      });
    }
    // 最后一张弃牌高亮（§7.1：金色描边）
    const ld = view.lastDiscard;
    if (ld !== null && !ld.claimed) {
      const band = this.riverBand(ld.seat);
      const p = players.find((x) => x.seat === ld.seat);
      const idx = p?.discards.length ?? 0;
      if (band !== null && idx > 0) {
        const shownIdx = Math.min(idx - 1, band.capacity - 1);
        const col = shownIdx % band.cols;
        const row = Math.floor(shownIdx / band.cols);
        const hl = new Graphics();
        hl.roundRect(band.x0 + col * band.stepX - 3, band.y0 + row * band.stepY - 3, RIVER.tile.w + 6, RIVER.tile.h + 6, 6);
        hl.fill(TABLE_COLOR.highlight, 0.35);
        this.riverC.addChild(hl);
      }
    }
  }

  /* ─────────────── 副露（§4.4 / §4.1 / §4.2） ─────────────── */

  private renderMelds(view: SeatView): void {
    this.meldC.removeChildren();

    // 自家：x∈[896,1240] y∈[480,560] 右对齐；极端 4 组自适应缩放（§4.4）
    const selfGroups = view.self.melds.map((m) =>
      this.meldGroup(m, TILE_SCALE.selfMeld, SELF_MELD.pengStep, SELF_MELD.gangStep, true),
    );
    if (selfGroups.length > 0) {
      const wrap = new Container();
      let cursorX = 0;
      selfGroups.forEach((g) => {
        g.position.set(cursorX, 0);
        cursorX += g.getLocalBounds().width + SELF_MELD.gap;
      });
      wrap.addChild(...selfGroups);
      const bounds = wrap.getLocalBounds();
      const scale = Math.min(1, AREA.selfMeld.w / Math.max(1, bounds.width));
      wrap.scale.set(scale);
      wrap.position.set(AREA.selfMeld.x + AREA.selfMeld.w - bounds.width * scale, AREA.selfMeld.y);
      this.meldC.addChild(wrap);
    }

    // 对家：居中 x=640 y=112，组间距 10（§4.1）
    const top = view.opponents.find((o) => o.seat === 2);
    if (top !== undefined && top.melds.length > 0) {
      const groups = top.melds.map((m) => this.meldGroup(m, TILE_SCALE.oppMeld, OPP_MELD_STEP.peng, OPP_MELD_STEP.gang, false));
      const widths = groups.map((g) => g.getLocalBounds().width);
      const total = widths.reduce((a, b) => a + b, 0) + TOP_MELD.gap * (groups.length - 1);
      let cx = TOP_MELD.x - total / 2;
      groups.forEach((g, i) => {
        g.position.set(cx, TOP_MELD.y);
        cx += widths[i]! + TOP_MELD.gap;
      });
      groups.forEach((g) => this.meldC.addChild(g));
    }

    // 右家：竖排 x=1140 y∈[220,520]（§4.2 镜像）
    const right = view.opponents.find((o) => o.seat === 1);
    if (right !== undefined && right.melds.length > 0) {
      const groups = right.melds.map((m) => this.meldGroup(m, TILE_SCALE.oppMeld, OPP_MELD_STEP.peng, OPP_MELD_STEP.gang, false));
      let cy = RIGHT_HAND.meldY;
      groups.forEach((g) => {
        g.position.set(RIGHT_HAND.meldX, cy);
        cy += g.getLocalBounds().height + SIDE_MELD.gap;
      });
      groups.forEach((g) => this.meldC.addChild(g));
    }

    // 左家：竖排 x=66 y∈[220,520]（§4.2）
    const left = view.opponents.find((o) => o.seat === 3);
    if (left !== undefined && left.melds.length > 0) {
      const groups = left.melds.map((m) => this.meldGroup(m, TILE_SCALE.oppMeld, OPP_MELD_STEP.peng, OPP_MELD_STEP.gang, false));
      let cy = LEFT_HAND.meldY;
      groups.forEach((g) => {
        g.position.set(LEFT_HAND.meldX, cy);
        cy += g.getLocalBounds().height + SIDE_MELD.gap;
      });
      groups.forEach((g) => this.meldC.addChild(g));
    }
  }

  /**
   * 一组副露：碰 3 张 / 杠 4 张；他家暗杠画 4 张牌背（§10 红线）。
   * 补杠（gang_bu）第 4 张侧躺（§4.4）。
   */
  private meldGroup(
    m: { readonly type: MeldType; readonly kind: TileKind | null; readonly tiles: readonly TileId[]; readonly claimedTileId: TileId | null },
    scale: number,
    pengStep: number,
    gangStep: number,
    isSelf: boolean,
  ): Container {
    const c = new Container();
    const isAnGang = m.kind === null && m.tiles.length === 0;

    // 角标文字：碰 → "碰" / 杠 → "杠" / 补杠 → "补" / 暗杠 → "暗"
    // 自家 + 他家都显示（暗杠隐藏 kind，但可显示"暗"作为动作类别；§10 红线）。
    const labelText = m.type === 'peng' ? '碰' : m.type === 'gang_bu' ? '补' : isAnGang ? '暗' : '杠';
    const labelStyle = {
      fontSize: isSelf ? 10 : 9,
      fill: isAnGang ? TABLE_COLOR.warning : TABLE_COLOR.gold,
      fontFamily: FONT_STACK,
      fontWeight: '700' as const,
    };
    const labelT = new Text({ text: labelText, style: labelStyle });
    labelT.position.set(0, -12);
    c.addChild(labelT);

    if (isAnGang) {
      // 暗杠（自家+他家）：4 张牌背 + 牌背颜色加深 + 暗杠外框
      const darkerBack = 0x0e3525; // 比普通牌背 #174a35 更深
      for (let i = 0; i < 4; i++) {
        const s = backSprite(scale);
        s.tint = darkerBack;
        s.position.set(i * (TILE_W * scale + 2), 0);
        c.addChild(s);
      }
      return c;
    }
    const tiles = m.tiles.length > 0 ? m.tiles : [];
    // 若 tiles 为空但 kind 已知（理论不可达），按 kind 生成占位 id
    const ids: readonly TileId[] = tiles.length > 0 ? tiles : this.fallbackMeldIds(m.kind, m.type);
    const step = m.type === 'peng' ? pengStep : gangStep;
    let slotX = 0;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]!;
      const s = tileSprite(kindOfTileId(id), scale);
      if (m.type === 'gang_bu' && i === ids.length - 1) {
        // 补杠第 4 张侧躺
        slotX += placeSideLie(s, slotX, 0, scale);
      } else {
        s.position.set(slotX, 0);
        slotX += TILE_W * scale;
      }
      c.addChild(s);
      // 后续张重叠：步长 = 露出宽度
      if (i < ids.length - 1) slotX -= TILE_W * scale - step;
    }
    return c;
  }

  /** kind 已知但 tiles 缺失时的兜底（仅防御）：取该牌面前 N 个实例 id。 */
  private fallbackMeldIds(kind: TileKind | null, type: MeldType): TileId[] {
    if (kind === null) return [];
    const count = type === 'peng' ? 3 : 4;
    const base = this.kindIndex(kind) * 4;
    const out: TileId[] = [];
    for (let i = 0; i < count; i++) out.push(base + i);
    return out;
  }

  private kindIndex(kind: TileKind): number {
    const suit = kind.slice(0, -1);
    const rank = Number(kind.slice(-1));
    const suitIdx = suit === 'wan' ? 0 : suit === 'tong' ? 1 : 2;
    return suitIdx * 9 + (rank - 1);
  }

  /* ─────────── 牌墙剩余（2026-08-12 腾讯麻将风格：金色牌背堆 + 文字） ─────────── */

  /** 牌墙剩余**视觉**：右下角一排金色牌背（最多 12 张），显示「剩余 N 张」+ 总张数。 */
  private renderWall(view: SeatView): void {
    this.wallC.removeChildren();
    // 位置：右下角（避开对面手牌/弃牌河/中央 UI）
    const baseX = 1100;
    const baseY = 24;
    const total = view.wallRemaining;
    const shown = Math.min(total, 12);
    // 金背矩形 24×34
    const tileW = 24;
    const tileH = 34;
    const gap = 4;
    for (let i = 0; i < shown; i++) {
      const g = new Graphics();
      g.roundRect(0, 0, tileW, tileH, 4);
      g.fill(0xe0a040);
      g.stroke({ width: 1, color: 0x8a6e1a });
      // 内部菱形
      g.poly([tileW / 2, 5, tileW - 4, tileH / 2, tileW / 2, tileH - 5, 4, tileH / 2]);
      g.fill({ color: 0xc89530, alpha: 0.8 });
      g.position.set(baseX + i * (tileW + gap), baseY);
      this.wallC.addChild(g);
    }
    // 文字：「剩余 N 张」
    const label = new Text({
      text: total > 12 ? `牌墙剩余 ${total} 张` : `牌墙剩余 ${total} 张`,
      style: { fontSize: 12, fill: TABLE_COLOR.gold, fontFamily: FONT_STACK, fontWeight: '700' },
    });
    label.anchor.set(1, 0);
    label.position.set(baseX + shown * (tileW + gap) - 4, baseY + tileH + 4);
    this.wallC.addChild(label);
  }

  /* ─────────────── 胡牌翻牌（2026-08-12 用户要求） ─────────────── */

  /** 渲染赢家面前的翻开 winningTile + 「胡」角标（来自 WinRecord）。 */
  private renderWinningTiles(view: SeatView): void {
    this.winningTileC.removeChildren();
    const records = this.collectWinRecords(view);
    // 按座位分组，叠放（多胡时同家多张）
    const perSeat = new Map<SeatIndex, TileId[]>();
    for (const r of records) {
      const arr = perSeat.get(r.seat);
      if (arr === undefined) {
        perSeat.set(r.seat, [r.tile]);
      } else {
        arr.push(r.tile);
      }
    }
    perSeat.forEach((tiles, seat) => {
      const pos = WIN_TILE_POS[seat];
      // 防御：seat 超出 0..3 时跳过（理论不可达）
      if (pos === undefined) return;
      tiles.forEach((tileId, i) => {
        const s = tileSprite(kindOfTileId(tileId), TILE_SCALE.river);
        // 多张叠加向右偏移
        s.position.set(pos.x + i * 24, pos.y);
        // 描边强调（金色发光圈）
        const hl = new Graphics();
        hl.roundRect(pos.x + i * 24 - 3, pos.y - 3, RIVER.tile.w + 6, RIVER.tile.h + 6, 6);
        hl.stroke({ width: 2, color: TABLE_COLOR.gold });
        hl.fill(TABLE_COLOR.gold, 0.2);
        this.winningTileC.addChild(hl);
        this.winningTileC.addChild(s);
      });
      // 「胡」角标（在第一张上方）
      const labelT = new Text({
        text: '胡',
        style: { fontSize: 16, fill: TABLE_COLOR.warning, fontFamily: FONT_STACK, fontWeight: '700' },
      });
      labelT.anchor.set(0.5);
      labelT.position.set(pos.x, pos.y - 24);
      this.winningTileC.addChild(labelT);
    });
  }

  /** 收集所有可见的胡牌记录（含自家 + 他家，结算阶段均可见）。 */
  private collectWinRecords(view: SeatView): readonly { readonly seat: SeatIndex; readonly tile: TileId }[] {
    const out: { seat: SeatIndex; tile: TileId }[] = [];
    for (const r of view.self.winRecords) out.push({ seat: r.seat, tile: r.winningTile });
    for (const o of view.opponents) {
      for (const r of o.winRecords) out.push({ seat: r.seat, tile: r.winningTile });
    }
    return out;
  }

  /** 多局重开前清空胡牌翻牌。 */
  resetWinningTiles(): void {
    this.winningTileC.removeChildren();
  }

  /** 多局重开前清空顶部已胡牌背堆。 */
  resetClaimed(): void {
    this.claimedC.removeChildren();
  }

  /* ─────────────── 中央信息区（§4.3：字号 28、--text-primary） ─────────────── */

  private renderPrompts(view: SeatView): void {
    this.promptC.removeChildren();
    const ui = this.latestUi;
    const text = ui?.phaseLabel ?? '';
    if (text.length > 0) {
      // 中央圆形 UI（2026-08-12 用户要求腾讯麻将风格）：
      // 深绿半透明圆形 + 金色边框 + phaseLabel 文字居中
      const cx = CENTER_INFO.x;
      const cy = CENTER_INFO.y;
      const r = 70;
      const bg = new Graphics();
      bg.circle(cx, cy, r);
      bg.fill({ color: 0x0b2b21, alpha: 0.85 });
      bg.stroke({ width: 3, color: TABLE_COLOR.gold });
      this.promptC.addChild(bg);
      // 上下小三角（参考腾讯风格：「<」 「>」环绕数字）
      const triUp = new Graphics();
      triUp.moveTo(cx, cy - r - 8);
      triUp.lineTo(cx - 6, cy - r - 16);
      triUp.lineTo(cx + 6, cy - r - 16);
      triUp.closePath();
      triUp.fill(TABLE_COLOR.gold);
      this.promptC.addChild(triUp);
      const triDown = new Graphics();
      triDown.moveTo(cx, cy + r + 8);
      triDown.lineTo(cx - 6, cy + r + 16);
      triDown.lineTo(cx + 6, cy + r + 16);
      triDown.closePath();
      triDown.fill(TABLE_COLOR.gold);
      this.promptC.addChild(triDown);

      const t = new Text({
        text,
        style: { fontSize: 16, fill: TABLE_COLOR.gold, fontFamily: FONT_STACK, fontWeight: '700' },
      });
      t.anchor.set(0.5);
      // 多行容错：长文字略小一点
      if (text.length > 6) t.style.fontSize = 13;
      t.position.set(cx, cy);
      this.promptC.addChild(t);
    }

    if (ui?.toast != null) {
      const tt = new Text({
        text: ui.toast,
        style: { fontSize: 15, fill: TABLE_COLOR.textPrimary, fontFamily: FONT_STACK },
      });
      tt.anchor.set(0.5);
      tt.position.set(CENTER_INFO.x, CENTER_INFO.y + 100);
      this.promptC.addChild(tt);
    }
    // 定缺提示（座位标记，--text-secondary）
    if (view.phase === 'void') {
      for (let s = 0 as SeatIndex; s < 4; s = (s + 1) as SeatIndex) {
        const suit = s === view.viewerSeat ? view.self.voidSuit : view.opponents.find((o) => o.seat === s)?.voidSuit ?? null;
        if (suit === null) continue;
        const seatPos = this.seatCenter(s);
        const mark = new Text({
          text: `缺${SUIT_LABEL[suit]}`,
          style: { fontSize: 14, fill: TABLE_COLOR.textSecondary, fontFamily: FONT_STACK, fontWeight: '700' },
        });
        mark.anchor.set(0.5);
        mark.position.set(seatPos.x, seatPos.y);
        this.promptC.addChild(mark);
      }
    }
  }

  private seatCenter(seat: SeatIndex): { readonly x: number; readonly y: number } {
    switch (seat) {
      case 0:
        return { x: BASE_W / 2, y: SELF_HAND.y - 90 };
      case 1:
        return { x: RIGHT_HAND.x - 90, y: BASE_H / 2 };
      case 2:
        return { x: BASE_W / 2, y: TOP_HAND.nameY - 8 };
      case 3:
        return { x: LEFT_HAND.x + 90, y: BASE_H / 2 };
    }
  }

  /* ─────────────── 操作按钮区（§4.5 右下角） ─────────────── */

  /** 定缺花色卡（§7.3：120×80，手牌区上方中央）。 */
  private voidCard(suit: Suit): Container {
    const c = new Container();
    const w = VOID_CARDS.w;
    const h = VOID_CARDS.h;
    const color = SUIT_COLOR[suit];
    const bg = new Graphics();
    bg.roundRect(0, 0, w, h, 10);
    bg.fill(0xf7f3e8);
    bg.stroke({ width: 3, color });
    const label = new Text({
      text: `缺${SUIT_LABEL[suit]}`,
      style: { fontSize: 22, fill: color, fontFamily: FONT_STACK, fontWeight: '700' },
    });
    label.anchor.set(0.5);
    label.position.set(w / 2, h / 2);
    c.addChild(bg, label);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(0, 0, w, h);
    c.on('pointertap', () => this.actionHandler?.({ type: 'declare_void', suit }));
    return c;
  }

  private renderActionBar(view: SeatView, ui: TableUiState): void {
    this.actionC.removeChildren();
    if (view.phase !== 'void' && view.phase !== 'playing') return;
    if (view.phase === 'void') {
      if (ui.needsVoid) {
        // 中央信息区 phaseLabel 已提示「请选择定缺花色」（§4.3）；此处只摆花色卡（§7.3）
        (['wan', 'tong', 'tiao'] as const).forEach((suit, i) => {
          const card = this.voidCard(suit);
          card.position.set(VOID_CARDS.x + (i - 1) * (VOID_CARDS.w + VOID_CARDS.gap), VOID_CARDS.y);
          this.actionC.addChild(card);
        });
      }
      return;
    }
    // playing：右下角按钮（碰/杠/胡 竖排 x=1000；PASS 与胡同行右侧 x=1144，§4.5）
    const button = (label: string, x: number, y: number, fill: number, textFill: number, onClick: () => void): void => {
      const b = makeButton(label, ACTION.w, ACTION.h, onClick, fill, textFill);
      b.position.set(x, y);
      this.actionC.addChild(b);
    };
    if (ui.claims.includes('peng')) {
      button('碰', ACTION.x, ACTION.y0, BUTTON_COLOR.action, BUTTON_COLOR.onAction, () => this.actionHandler?.({ type: 'peng' }));
    }
    if (ui.claims.includes('gang') || ui.canSelfGang) {
      const kinds = ui.gangKinds.length > 0 ? ui.gangKinds : ui.selfGangKinds;
      button('杠', ACTION.x, ACTION.y0 + ACTION.stepY, BUTTON_COLOR.action, BUTTON_COLOR.onAction, () => {
        const kind = kinds[0];
        if (kind !== undefined) this.actionHandler?.({ type: 'gang', kind });
      });
    }
    if (ui.claims.includes('hu') || ui.canZimoHu) {
      button('胡', ACTION.x, ACTION.y0 + ACTION.stepY * 2, BUTTON_COLOR.hu, BUTTON_COLOR.onHu, () => this.actionHandler?.({ type: 'hu' }));
    }
    if (ui.claims.includes('pass')) {
      button('过', ACTION.passX, ACTION.passY, BUTTON_COLOR.pass, BUTTON_COLOR.onPass, () => this.actionHandler?.({ type: 'pass' }));
    }
    // 出牌提示（点击即出，§4.5 P0）
    if (ui.canDiscard) {
      const hint = new Text({
        text: '点击可打牌直接打出',
        style: { fontSize: 13, fill: TABLE_COLOR.textSecondary, fontFamily: FONT_STACK },
      });
      hint.anchor.set(0.5);
      hint.position.set(ACTION.x + ACTION.w / 2, ACTION.y0 - 14);
      this.actionC.addChild(hint);
    }
  }

  /* ─────────────── 结算面板（§4.7） ─────────────── */

  private renderSettlement(view: SeatView): void {
    if (view.settlement === null && view.phase !== 'finished') {
      this.settledRendered = false;
      return;
    }
    if (this.settledRendered) return;
    this.settledRendered = true;

    this.settleC.removeChildren();
    const overlay = new Graphics();
    overlay.roundRect(SETTLE.x, SETTLE.y, SETTLE.w, SETTLE.h, SETTLE.radius);
    overlay.fill(SETTLE.fill, SETTLE.fillAlpha);
    overlay.stroke({ width: 2, color: SETTLE.border, alpha: SETTLE.borderAlpha });
    this.settleC.addChild(overlay);

    const cx = SETTLE.x + SETTLE.w / 2;
    const title = new Text({
      text: view.settlement !== null ? '血战到底 · 结算' : '对局结束',
      style: { fontSize: 26, fill: TABLE_COLOR.gold, fontFamily: FONT_STACK, fontWeight: '700' },
    });
    title.anchor.set(0.5);
    title.position.set(cx, SETTLE.y + 50);
    this.settleC.addChild(title);

    const s = view.settlement;
    if (s !== null) {
      const order: readonly SeatIndex[] = [0, 1, 2, 3];
      order.forEach((seat, i) => {
        const y = SETTLE.y + 120 + i * 62;
        const name = seat === view.viewerSeat ? '我' : this.playerName(seat, view);
        const net = s.netScores[seat];
        const tags: string[] = [];
        if (s.dajiaoSeats.includes(seat)) tags.push('查大叫');
        if (s.flowerPigSeats.includes(seat)) tags.push('花猪');
        const line = new Text({
          text: `${name}    ${net >= 0 ? '+' : ''}${net}  ${tags.join(' ')}`,
          style: { fontSize: 18, fill: seat === view.viewerSeat ? TABLE_COLOR.gold : TABLE_COLOR.textPrimary, fontFamily: FONT_STACK, fontWeight: '700' },
        });
        line.position.set(SETTLE.x + 60, y);
        this.settleC.addChild(line);
      });
      const detailTitle = new Text({
        text: '明细',
        style: { fontSize: 14, fill: TABLE_COLOR.textSecondary, fontFamily: FONT_STACK, fontWeight: '700' },
      });
      detailTitle.position.set(SETTLE.x + 60, SETTLE.y + 380);
      this.settleC.addChild(detailTitle);
      s.transfers.slice(0, 6).forEach((tr, i) => {
        const detail = new Text({
          text: `${this.seatName(tr.from, view)} → ${this.seatName(tr.to, view)}  ${tr.amount >= 0 ? '+' : ''}${tr.amount}  ${tr.reason}`,
          style: { fontSize: 12, fill: TABLE_COLOR.textSecondary, fontFamily: FONT_STACK },
        });
        detail.position.set(SETTLE.x + 60, SETTLE.y + 404 + i * 20);
        this.settleC.addChild(detail);
      });
    }
  }

  /** 多局制：开启新局前清空结算面板（否则上一局 overlay 残留）。 */
  resetSettlement(): void {
    this.settledRendered = false;
    this.settleC.removeChildren();
  }

  /* ─────────────── 小工具 ─────────────── */

  /** 名牌 + 分数 + 定缺标记 + 圆形头像占位（§4.1 名牌行内容；§10 分数正负色 + 符号）。2026-08-12 加圆形头像。 */
  private nameRow(
    name: string,
    score: number,
    voidSuit: Suit | null,
    x: number,
    y: number,
    align: 'center' | 'left' | 'right',
  ): Container {
    const c = new Container();
    const nameStyle = { fontSize: NAME_FONT, fill: TABLE_COLOR.textPrimary, fontFamily: FONT_STACK, fontWeight: '700' as const };
    const scoreText = `${score >= 0 ? '+' : ''}${score}`;
    const scoreStyle = {
      fontSize: 13,
      fill: score >= 0 ? TABLE_COLOR.scorePos : TABLE_COLOR.scoreNeg,
      fontFamily: FONT_STACK,
      fontWeight: '700' as const,
    };
    const nameW = measureTextWidth(name, NAME_FONT, '700');
    const scoreW = measureTextWidth(scoreText, 13, '700');
    const gap = 6;
    // 圆形头像占位（直径 28 = 半径 14）+ 文字间距 8
    const avatarR = 14;
    const avatarGap = 8;
    const badgeW = voidSuit !== null ? measureTextWidth(`缺${SUIT_LABEL[voidSuit]}`, 11, '700') + 8 : 0;
    const totalW = avatarR * 2 + avatarGap + nameW + gap + scoreW + (badgeW > 0 ? badgeW + 10 : 0);
    const left = align === 'center' ? x - totalW / 2 : align === 'left' ? x : x - totalW;

    // 圆形头像占位（深绿底 + 金色描边 + 简笔人形）
    const avatarX = left + avatarR;
    const avatarBg = new Graphics();
    avatarBg.circle(avatarX, y, avatarR);
    avatarBg.fill({ color: 0x1c6a4c, alpha: 0.85 });
    avatarBg.stroke({ width: 1.5, color: TABLE_COLOR.gold });
    c.addChild(avatarBg);
    // 简笔人形（头+肩）：一个圆（头）+ 半圆/椭圆（肩）
    const head = new Graphics();
    head.circle(avatarX, y - 4, 4);
    head.fill(TABLE_COLOR.textPrimary);
    c.addChild(head);
    const shoulders = new Graphics();
    shoulders.arc(avatarX, y + 8, 8, Math.PI, 0);
    shoulders.fill(TABLE_COLOR.textPrimary);
    c.addChild(shoulders);

    // 文字起点（头像右侧）
    const textLeft = left + avatarR * 2 + avatarGap;
    const nameT = new Text({ text: name, style: nameStyle });
    nameT.anchor.set(0, 0.5);
    nameT.position.set(textLeft, y);
    const scoreT = new Text({ text: scoreText, style: scoreStyle });
    scoreT.anchor.set(0, 0.5);
    scoreT.position.set(textLeft + nameW + gap, y);
    c.addChild(nameT, scoreT);

    // 定缺花色徽章（§4.1 名牌行扩展）：玩家已选缺门 → 在名牌右侧画「缺 X」小标
    if (voidSuit !== null) {
      const suitLabel = SUIT_LABEL[voidSuit];
      const badgeText = `缺${suitLabel}`;
      const badgeStyle = {
        fontSize: 11,
        fill: SUIT_COLOR[voidSuit],
        fontFamily: FONT_STACK,
        fontWeight: '700' as const,
      };
      const badgeX = textLeft + nameW + gap + scoreW + 10;
      const badgeY = y - 8;
      const badgeBg = new Graphics();
      badgeBg.roundRect(badgeX - 4, badgeY - 2, badgeW, 16, 4);
      badgeBg.fill({ color: TABLE_COLOR.panel, alpha: 0.85 });
      badgeBg.stroke({ width: 1, color: SUIT_COLOR[voidSuit], alpha: 0.6 });
      c.addChild(badgeBg);
      const badgeT = new Text({ text: badgeText, style: badgeStyle });
      badgeT.anchor.set(0, 0.5);
      badgeT.position.set(badgeX, y);
      c.addChild(badgeT);
    }
    return c;
  }

  private playerName(seat: SeatIndex, view: SeatView): string {
    if (seat === view.viewerSeat) return '我';
    const o = view.opponents.find((x) => x.seat === seat);
    if (o === undefined) return `座位${seat + 1}`;
    const nick = o.playerId.replace(/^bot:/, 'AI·').slice(0, 8);
    return `${nick}${o.hasWon ? '(胡)' : ''}${o.isRobot ? '·AI' : ''}`;
  }

  private seatName(seat: SeatIndex, view: SeatView): string {
    return seat === view.viewerSeat ? '我' : this.playerName(seat, view).replace(/\(胡\)|·AI/g, '');
  }
}
